import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, GuardResult, Router } from '@angular/router';
import {
    AuthGuard, AuthService,
    OccEndpointsService,
    ProtectedRoutesGuard,
    ProtectedRoutesService, SemanticPathService,
    WindowRef
} from '@spartacus/core';
import { combineLatest, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { AsmEnablerService, CsAgentAuthService } from '@spartacus/asm/root';
import { SikoPublicUrlsService } from '../routing/siko-public-urls.service';

@Injectable({ providedIn: 'root' })
export class SikoB2cProtectedRoutesGuard extends ProtectedRoutesGuard {
    constructor(
        protected service: ProtectedRoutesService,
        protected authGuard: AuthGuard,
        protected http: HttpClient,
        protected occEndpoints: OccEndpointsService,
        protected sikoPublicUrlsService: SikoPublicUrlsService,
        protected winRef: WindowRef,
        protected router: Router,
        protected asmEnablerService: AsmEnablerService,
        protected authService: AuthService,
        protected semanticPathService: SemanticPathService,
        protected csAgentAuthService: CsAgentAuthService,
    ) {
        super(service, authGuard);
    }

    canActivate(route: ActivatedRouteSnapshot): Observable<GuardResult> {
        let urlSegments: string[] = route.url.map(seg => seg.path);

        urlSegments = urlSegments.length ? urlSegments : [''];

        const [currentUrl] = urlSegments;

        return this.sikoPublicUrlsService
            .getPublicUrls()
            .pipe(switchMap((publicUrls: string[]) => this.processUrls(publicUrls, currentUrl, urlSegments)));
    }

    processUrls(publicUrls: string[], currentUrl: string, urlSegments: string[]): Observable<GuardResult> {
        if (publicUrls.includes(currentUrl) && currentUrl) {
            return of(true);
        } else if (this.service.isUrlProtected(urlSegments)) {
            /**
             ASM does not initialise correctly when the application is loaded on a protected route.
             This can happen either when:
             the user manually navigates to a protected route with ?asm=true, or ASM is enabled in local storage and the user reopens
             the application on a protected route
             (for example after closing the browser/tab while ASM was active).

             In these cases, the protected route guard redirects before ASM has a chance to bootstrap,
             leaving the agent in a broken state.

             To prevent this, when ASM activation is detected but no CS agent session is active yet,
             the user is redirected to /login. Since /login is a public route, ASM can initialise
             correctly there before the agent logs in and starts customer emulation.
             */
            return combineLatest([
                this.csAgentAuthService.isCustomerSupportAgentLoggedIn(),
                this.authService.isUserLoggedIn(),
            ]).pipe(
                take(1),
                switchMap(([isAgentLoggedIn, isUserLoggedIn]) => {
                    if (!isAgentLoggedIn && !isUserLoggedIn) {
                        if (this.asmEnablerService.isEnabled()) {
                            return of(this.router.parseUrl('/login'));
                        }
                    }

                    return this.authGuard.canActivate();
                }),
            );
        }

        return of(true);
    }
}
